/**
 * useCacheStorage Hook
 * Manages localStorage caching with freshness detection
 * Includes quota protection and size limits for production safety
 */

import { useState, useCallback } from 'react';

// Maximum cache size in bytes (2MB - safe for all browsers)
const MAX_CACHE_SIZE_BYTES = 2 * 1024 * 1024;

/**
 * Calculate the size of a string in bytes
 * @param {string} str - String to measure
 * @returns {number} Size in bytes
 */
const getStringSizeBytes = (str) => {
    return new Blob([str]).size;
};

/**
 * Get total localStorage usage in bytes
 * @returns {number} Total bytes used
 */
const getLocalStorageUsage = () => {
    let total = 0;
    try {
        for (let key in localStorage) {
            if (localStorage.hasOwnProperty(key)) {
                total += getStringSizeBytes(localStorage[key]);
                total += getStringSizeBytes(key);
            }
        }
    } catch (e) {
        console.warn('[Cache] Could not calculate storage usage:', e);
    }
    return total;
};

/**
 * Format bytes as human-readable string
 * @param {number} bytes - Bytes to format
 * @returns {string} Formatted string (e.g., "1.5 MB")
 */
const formatBytes = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

/**
 * Cache storage hook with localStorage support
 *
 * @param {Object} config - Configuration object
 * @param {string} config.cacheKey - localStorage cache key
 * @param {string} config.lastFetchKey - localStorage last fetch date key
 * @param {number} config.staleThreshold - Stale threshold in milliseconds (default: 10 minutes)
 * @returns {Object} Cache management functions
 */
const useCacheStorage = ({
    cacheKey = 'dashboard-cache',
    lastFetchKey = 'dashboard-last-fetch',
    staleThreshold = 10 * 60 * 1000
}) => {
    const [cacheInfo, setCacheInfo] = useState(null);

    /**
     * Calculate data age in seconds
     */
    const calculateDataAge = useCallback((savedAt) => {
        const savedDate = new Date(savedAt);
        const now = new Date();
        return Math.floor((now - savedDate) / 1000);
    }, []);

    /**
     * Format cache age as human-readable string
     */
    const formatCacheAge = useCallback((seconds) => {
        if (!seconds || seconds < 0) return 'just now';
        if (seconds < 60) return 'less than a minute ago';
        if (seconds < 120) return '1 minute ago';
        if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
        if (seconds < 7200) return '1 hour ago';
        if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
        return `${Math.floor(seconds / 86400)} days ago`;
    }, []);

    /**
     * Load from Azure Table Storage (stub - returns null)
     * Kept for API compatibility but no longer uses Azure
     */
    const loadFromAzure = useCallback(async (tenantId, dateKey) => {
        // Azure storage removed - always return null to fall back to localStorage
        return null;
    }, []);

    /**
     * Save to Azure Table Storage (stub - no-op)
     * Kept for API compatibility but no longer uses Azure
     */
    const saveToAzure = useCallback(async (tenantId, dateKey, snapshot) => {
        // Azure storage removed - no-op
    }, []);

    /**
     * Load from localStorage
     */
    const loadFromLocalStorage = useCallback((configVersion = 1) => {
        try {
            const cached = localStorage.getItem(cacheKey);
            if (!cached) return null;

            const cachedData = JSON.parse(cached);
            const cachedVersion = cachedData.configVersion || 0;

            // Auto-invalidate if version mismatch
            if (cachedVersion !== configVersion) {
                localStorage.removeItem(cacheKey);
                localStorage.removeItem(lastFetchKey);
                return null;
            }

            if (cachedData.snapshot && cachedData.savedAt) {
                const ageSeconds = calculateDataAge(cachedData.savedAt);

                return {
                    snapshot: cachedData.snapshot,
                    savedAt: cachedData.savedAt,
                    age: ageSeconds
                };
            }
            return null;
        } catch (error) {
            return null;
        }
    }, [cacheKey, lastFetchKey, calculateDataAge]);

    /**
     * Save to localStorage with quota protection
     * - Validates size before saving
     * - Handles QuotaExceededError gracefully
     * - Logs storage usage for monitoring
     */
    const saveToLocalStorage = useCallback((snapshot, configVersion = 1) => {
        try {
            const now = new Date().toISOString();
            const dataToSave = {
                snapshot,
                savedAt: now,
                configVersion
            };
            const serialized = JSON.stringify(dataToSave);
            const sizeBytes = getStringSizeBytes(serialized);

            // Check size before saving
            if (sizeBytes > MAX_CACHE_SIZE_BYTES) {
                return false;
            }

            localStorage.setItem(cacheKey, serialized);
            localStorage.setItem(lastFetchKey, now);

            return true;
        } catch (error) {
            // Handle quota exceeded error
            if (error.name === 'QuotaExceededError' || error.code === 22) {
                try {
                    localStorage.removeItem(cacheKey);
                    localStorage.removeItem(lastFetchKey);
                    // Try once more after clearing
                    const now = new Date().toISOString();
                    localStorage.setItem(cacheKey, JSON.stringify({ snapshot, savedAt: now, configVersion }));
                    localStorage.setItem(lastFetchKey, now);
                    return true;
                } catch (retryError) {
                    return false;
                }
            }
            return false;
        }
    }, [cacheKey, lastFetchKey]);

    /**
     * Check if data is stale
     */
    const isStale = useCallback((ageSeconds) => {
        return (ageSeconds * 1000) >= staleThreshold;
    }, [staleThreshold]);

    /**
     * Get freshness status
     */
    const getFreshnessStatus = useCallback(() => {
        if (!cacheInfo || cacheInfo.age === null || cacheInfo.age === undefined) {
            return { status: 'unknown', label: 'Loading...', color: '#64748b' };
        }

        const ageMs = cacheInfo.age * 1000;

        if (ageMs < 2 * 60 * 1000) {
            return { status: 'live', label: 'LIVE', color: '#10b981' };
        } else if (ageMs < staleThreshold) {
            return { status: 'cached', label: 'CACHED', color: '#f59e0b' };
        } else {
            return { status: 'stale', label: 'STALE', color: '#ef4444' };
        }
    }, [cacheInfo, staleThreshold]);

    /**
     * Get storage usage statistics
     * @returns {Object} Storage stats for monitoring
     */
    const getStorageStats = useCallback(() => {
        try {
            const cacheData = localStorage.getItem(cacheKey);
            const cacheSize = cacheData ? getStringSizeBytes(cacheData) : 0;
            const totalUsage = getLocalStorageUsage();

            return {
                cacheSize,
                totalUsage,
                maxCacheSize: MAX_CACHE_SIZE_BYTES,
                formatted: {
                    cache: formatBytes(cacheSize),
                    total: formatBytes(totalUsage),
                    max: formatBytes(MAX_CACHE_SIZE_BYTES),
                    percentUsed: ((cacheSize / MAX_CACHE_SIZE_BYTES) * 100).toFixed(1) + '%'
                }
            };
        } catch (e) {
            console.warn('[Cache] Could not get storage stats:', e);
            return null;
        }
    }, [cacheKey]);

    /**
     * Clear all dashboard-related cache
     */
    const clearCache = useCallback(() => {
        try {
            localStorage.removeItem(cacheKey);
            localStorage.removeItem(lastFetchKey);
            setCacheInfo(null);
            return true;
        } catch (e) {
            return false;
        }
    }, [cacheKey, lastFetchKey]);

    return {
        cacheInfo,
        setCacheInfo,
        loadFromAzure,
        saveToAzure,
        loadFromLocalStorage,
        saveToLocalStorage,
        calculateDataAge,
        formatCacheAge,
        isStale,
        getFreshnessStatus,
        getStorageStats,
        clearCache
    };
};

export default useCacheStorage;
